// src/App.js - Updated with Time Tracking routes
import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ToastProvider } from "./contexts/ToastContext";
import { LandingPage } from "./components/LandingPage";
import { LoginPage } from "./components/LoginPage";
import { SignupPage } from "./components/SignupPage";
import { DiscoveryPage } from "./components/DiscoveryPage";
import { Dashboard } from "./components/Dashboard";
import { EmployeeList } from "./components/EmployeeList";
import { EmployeeDetail } from "./components/EmployeeDetail";
import { AddEmployee } from "./components/AddEmployee";
import { TimeTrackingRoutes } from "./components/TimeTrackingRoutes";
import { NotFoundPage } from "./components/NotFoundPage";
import { LoadingScreen } from "./components/LoadingScreen";
import { AppLayout } from "./components/AppLayout";
import { ScrollToTop } from "./components/ScrollToTop";
import { useAuth, AuthProvider } from "./hooks/useAuth";
import { useCompanyValidation } from "./hooks/useCompanyValidation";
import { useParams } from "react-router-dom";
import { ResetPasswordPage } from "./components/ResetPasswordPage";
import { VerifyEmailPage } from "./components/VerifyEmailPage";
import { TermsPage } from "./components/legal/TermsPage";
import { PrivacyPage } from "./components/legal/PrivacyPage";
import { DpaPage } from "./components/legal/DpaPage";
import { ProcessPayrollRoute } from "./components/payroll/ProcessPayrollRoute";
import PayrollList from "./components/payroll/PayrollList";
import PayrollRatesPage from "./components/settings/PayrollRatesPage";
import HolidayCalendarPage from "./components/settings/HolidayCalendarPage";
import SettingsPage from "./components/settings/SettingsPage";
import CsvExportLayoutPage from "./components/settings/CsvExportLayoutPage";
import DepartmentsPage from "./components/settings/DepartmentsPage";
import TeamPage from "./components/settings/TeamPage";
import SubscriptionsPage from "./components/settings/SubscriptionsPage";
import InvoiceHistoryPage from "./components/settings/InvoiceHistoryPage";
import AttendancePolicyPage from "./components/settings/AttendancePolicyPage";
import PayslipSettingsPage from "./components/settings/PayslipSettingsPage";
import RoleEditorPage from "./components/settings/RoleEditorPage";
import RolesPage from "./components/settings/RolesPage";
import KioskManagementPage from "./components/settings/KioskManagementPage";
import OTApprovalPage from "./components/OTApprovalPage";
import AdjustmentsPage from "./components/adjustments/AdjustmentsPage";
import WorkSchedulePage from "./components/employees/WorkSchedulePage";
import LoansPage from "./components/employees/LoansPage";
import PayrollApprovalPage from "./components/PayrollApprovalPage";
import PayslipList from "./components/payroll/PayslipList";
import PermissionGuard from "./components/PermissionGuard";
import { PERMISSIONS, ANY_SETTINGS_PERMISSION, REPORTS_HUB_ANY } from "./config/permissions";
import { DTR_REPORT_REQUIRES, CONTRIBUTION_BASIS_REPORT_REQUIRES, LOAN_REMITTANCE_REPORT_REQUIRES, CONTRIBUTION_SCHEDULE_REPORT_REQUIRES } from "./config/reportCards";
import ReportsHubPage from "./components/reports/ReportsHubPage";
import DtrReportPage from "./components/reports/DtrReportPage";
import AttendanceSummaryReportPage from "./components/reports/AttendanceSummaryReportPage";
import ContributionBasisReportPage from "./components/reports/ContributionBasisReportPage";
import LoanRemittanceReportPage from "./components/reports/LoanRemittanceReportPage";
import ContributionSchedulePage from "./components/reports/ContributionSchedulePage";

// Company Route Guard Component
const CompanyRouteGuard = ({ children }) => {
  const { slug } = useParams();
  const { user, company: userCompany, loading: authLoading } = useAuth();

  // Only validate company after auth is confirmed
  // This avoids unnecessary Firestore queries for unauthenticated users
  const shouldValidateCompany = !authLoading && !!user;
  const {
    company: validatedCompany,
    loading: companyLoading,
    error,
  } = useCompanyValidation(shouldValidateCompany ? slug : null);

  // Wait for auth first
  if (authLoading) {
    return <LoadingScreen message="Loading..." />;
  }

  // Check authentication before any Firestore operations
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Now wait for company validation (only runs after auth confirmed)
  if (companyLoading) {
    return <LoadingScreen message="Loading..." />;
  }

  // Check if company exists
  if (!validatedCompany || error) {
    return <NotFoundPage type="company" slug={slug} />;
  }

  // Check if user has access to this company
  if (userCompany?.slug !== slug) {
    return <NotFoundPage type="company" slug={slug} />;
  }

  // All checks passed - render children
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { user, company, loading } = useAuth();

  if (loading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // If user is logged in, redirect to their dashboard
  if (user && company) {
    return <Navigate to={`/company/${company.slug}/dashboard`} replace />;
  }

  return children;
};

// Settings index — picks the first sub-page the caller has access to,
// instead of always redirecting to Departments. Without this, a user
// with e.g. only set:hol:view clicks Settings → /settings/departments →
// PermissionGuard blocks → silent redirect to dashboard. Keep this list
// in sync with the <Route path="settings"> children below.
const SettingsIndexRedirect = () => {
  const { hasAnyPermission } = useAuth();
  // Order mirrors the Settings sidebar (Company group first: Departments → Team
  // → Roles → Attendance Policy → Holidays; then Payroll group: Pay Rates →
  // CSV Export → Payslips; then Subscriptions → Invoice History). Matches
  // src/config/matrix.js settings subItems order.
  const subRoutes = [
    { path: 'departments', perms: [PERMISSIONS.SETTINGS_DEPARTMENTS, PERMISSIONS.SETTINGS_DEPARTMENTS_VIEW] },
    { path: 'team', perms: [PERMISSIONS.SETTINGS_TEAM_VIEW] },
    { path: 'roles', perms: [PERMISSIONS.SETTINGS_ROLES_EDIT, PERMISSIONS.SETTINGS_ROLES_VIEW] },
    { path: 'attendance-policy', perms: [PERMISSIONS.SETTINGS_ATTENDANCE_POLICY, PERMISSIONS.SETTINGS_ATTENDANCE_POLICY_VIEW] },
    { path: 'holidays', perms: [PERMISSIONS.SETTINGS_HOLIDAYS, PERMISSIONS.SETTINGS_HOLIDAYS_VIEW] },
    { path: 'payroll-rates', perms: [PERMISSIONS.SETTINGS_PAYROLL_RATES, PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW] },
    { path: 'csv-export', perms: [PERMISSIONS.SETTINGS_CSV_EXPORT, PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW] },
    { path: 'payslips', perms: [PERMISSIONS.SETTINGS_PAYSLIPS, PERMISSIONS.SETTINGS_PAYSLIPS_VIEW] },
    { path: 'subscriptions', perms: [PERMISSIONS.SETTINGS_PAYMENTS] },
    { path: 'invoices', perms: [PERMISSIONS.SETTINGS_PAYMENTS] },
  ];
  const firstAllowed = subRoutes.find((r) => hasAnyPermission(r.perms));
  // Outer PermissionGuard already verified the caller has ANY_SETTINGS_PERMISSION,
  // so firstAllowed should always be defined. Fall through to departments as a
  // last-resort fallback (which will then PermissionGuard-bounce if reached).
  return <Navigate to={firstAllowed ? firstAllowed.path : 'departments'} replace />;
};

function App() {
  return (
    <ToastProvider>
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />

          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          {/* Invite-only sign-up (PRD_SignupInvite C10). No PublicRoute: signing
              in sets useAuth's loading, which would remount the page and re-check
              a now-used link. The page redirects to / without an invite. */}
          <Route path="/signup" element={<SignupPage />} />

          <Route
            path="/discovery"
            element={
              <PublicRoute>
                <DiscoveryPage />
              </PublicRoute>
            }
          />

          <Route
            path="/auth/action"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />

          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Public legal pages — reachable in EVERY session state (logged-out,
              logged-in verified/unverified, stale session, unauthenticated).
              Bare routes (no PublicRoute/CompanyRouteGuard) + a matching exemption
              in useAuth.js. See PRD_LegalConsent.md C2. */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/dpa" element={<DpaPage />} />

          {/* Protected company routes with AppLayout wrapper */}
          <Route
            path="/company/:slug"
            element={
              <CompanyRouteGuard>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </CompanyRouteGuard>
            }
          >
            {/* Nested routes that will use the AppLayout */}
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="employees" element={
              <PermissionGuard permissions={[PERMISSIONS.EMPLOYEES_EDIT, PERMISSIONS.EMPLOYEES_VIEW]}><EmployeeList /></PermissionGuard>
            } />
            <Route path="employees/add" element={
              <PermissionGuard permission={PERMISSIONS.EMPLOYEES_EDIT}><AddEmployee key="add" /></PermissionGuard>
            } />
            <Route
              path="employees/:employeeId/edit"
              element={
                <PermissionGuard permission={PERMISSIONS.EMPLOYEES_EDIT}><AddEmployee key="edit" mode="edit" /></PermissionGuard>
              }
            />
            <Route
              path="employees/:employeeId/view"
              element={
                <PermissionGuard permission={PERMISSIONS.EMPLOYEES_VIEW}><EmployeeDetail /></PermissionGuard>
              }
            />
            {/* Work Schedule — a dual page under Employees (PRD_WorkScheduleManagement C10) */}
            <Route path="work-schedule" element={
              <PermissionGuard permissions={[PERMISSIONS.SCHEDULE_EDIT, PERMISSIONS.SCHEDULE_VIEW]}><WorkSchedulePage /></PermissionGuard>
            } />
            {/* Loans Management — a dual page under Employees (PRD_LoansManagement C12) */}
            <Route path="loans" element={
              <PermissionGuard permissions={[PERMISSIONS.LOANS_EDIT, PERMISSIONS.LOANS_VIEW]}><LoansPage /></PermissionGuard>
            } />

            {/* Time Tracking routes */}
            <Route path="time-tracking/*" element={
              <PermissionGuard permissions={[PERMISSIONS.TIME_TRACKING_EDIT, PERMISSIONS.TIME_TRACKING_VIEW]}><TimeTrackingRoutes /></PermissionGuard>
            } />

            {/* OT Approval route */}
            <Route path="ot-approval" element={
              <PermissionGuard permissions={[PERMISSIONS.TIME_OT_EDIT, PERMISSIONS.TIME_OT_VIEW]}><OTApprovalPage /></PermissionGuard>
            } />

            {/* Kiosks route — top-level under Time & Attendance (C16) */}
            <Route path="kiosks" element={
              <PermissionGuard permissions={[PERMISSIONS.KIOSK_EDIT, PERMISSIONS.KIOSK_VIEW]}><KioskManagementPage /></PermissionGuard>
            } />

            {/* Payroll routes */}
            <Route path="payroll" element={
              <PermissionGuard permission={PERMISSIONS.PAYROLL_VIEW}><PayrollList /></PermissionGuard>
            } />
            <Route path="payroll/process" element={
              <PermissionGuard permission={PERMISSIONS.PAYROLL_RUN}><ProcessPayrollRoute /></PermissionGuard>
            } />
            <Route path="payroll/payslips/:runId" element={
              <PermissionGuard permission={PERMISSIONS.PAYSLIP_VIEW}><PayslipList /></PermissionGuard>
            } />
            <Route path="payroll-approval" element={
              <PermissionGuard permission={PERMISSIONS.PAYROLL_APPROVE}><PayrollApprovalPage /></PermissionGuard>
            } />
            <Route path="adjustments" element={
              <PermissionGuard permissions={[PERMISSIONS.ADJUSTMENTS_EDIT, PERMISSIONS.ADJUSTMENTS_VIEW]}><AdjustmentsPage /></PermissionGuard>
            } />

            {/* Reports routes (PRD_DtrReport C1-C3). Each report route guard
                uses the SAME requires array its card declares (reportCards.js),
                so the config and the route gate cannot drift; requireAll, as
                the hub's card filter does. The hub itself opens to anyone who
                can open some card — REPORTS_HUB_ANY, any-of, the same array as
                the Reports nav entry (PRD_ContributionBasisReport C6). */}
            <Route path="reports" element={
              <PermissionGuard permissions={REPORTS_HUB_ANY}><ReportsHubPage /></PermissionGuard>
            } />
            <Route path="reports/dtr" element={
              <PermissionGuard permissions={DTR_REPORT_REQUIRES} requireAll><DtrReportPage /></PermissionGuard>
            } />
            <Route path="reports/attendance-summary" element={
              <PermissionGuard permissions={DTR_REPORT_REQUIRES} requireAll><AttendanceSummaryReportPage /></PermissionGuard>
            } />
            <Route path="reports/contribution-basis" element={
              <PermissionGuard permissions={CONTRIBUTION_BASIS_REPORT_REQUIRES} requireAll><ContributionBasisReportPage /></PermissionGuard>
            } />
            <Route path="reports/loan-remittance" element={
              <PermissionGuard permissions={LOAN_REMITTANCE_REPORT_REQUIRES} requireAll><LoanRemittanceReportPage /></PermissionGuard>
            } />
            <Route path="reports/contribution-schedule" element={
              <PermissionGuard permissions={CONTRIBUTION_SCHEDULE_REPORT_REQUIRES} requireAll><ContributionSchedulePage /></PermissionGuard>
            } />

            {/* Settings routes */}
            <Route path="settings" element={
              <PermissionGuard permissions={ANY_SETTINGS_PERMISSION}><SettingsPage /></PermissionGuard>
            }>
              <Route index element={<SettingsIndexRedirect />} />
              <Route path="departments" element={
                <PermissionGuard permissions={[PERMISSIONS.SETTINGS_DEPARTMENTS, PERMISSIONS.SETTINGS_DEPARTMENTS_VIEW]}><DepartmentsPage /></PermissionGuard>
              } />
              <Route path="payroll-rates" element={
                <PermissionGuard permissions={[PERMISSIONS.SETTINGS_PAYROLL_RATES, PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW]}><PayrollRatesPage /></PermissionGuard>
              } />
              <Route path="attendance-policy" element={
                <PermissionGuard permissions={[PERMISSIONS.SETTINGS_ATTENDANCE_POLICY, PERMISSIONS.SETTINGS_ATTENDANCE_POLICY_VIEW]}><AttendancePolicyPage /></PermissionGuard>
              } />
              <Route path="holidays" element={
                <PermissionGuard permissions={[PERMISSIONS.SETTINGS_HOLIDAYS, PERMISSIONS.SETTINGS_HOLIDAYS_VIEW]}><HolidayCalendarPage /></PermissionGuard>
              } />
              <Route path="csv-export" element={
                <PermissionGuard permissions={[PERMISSIONS.SETTINGS_CSV_EXPORT, PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW]}><CsvExportLayoutPage /></PermissionGuard>
              } />
              <Route path="payslips" element={
                <PermissionGuard permissions={[PERMISSIONS.SETTINGS_PAYSLIPS, PERMISSIONS.SETTINGS_PAYSLIPS_VIEW]}><PayslipSettingsPage /></PermissionGuard>
              } />
              <Route path="team" element={
                <PermissionGuard permissions={[PERMISSIONS.SETTINGS_TEAM_EDIT, PERMISSIONS.SETTINGS_TEAM_VIEW]}><TeamPage /></PermissionGuard>
              } />
              <Route path="roles" element={
                <PermissionGuard permissions={[PERMISSIONS.SETTINGS_ROLES_EDIT, PERMISSIONS.SETTINGS_ROLES_VIEW]}><RolesPage /></PermissionGuard>
              } />
              <Route path="roles/new" element={
                <PermissionGuard permission={PERMISSIONS.SETTINGS_ROLES_EDIT}><RoleEditorPage /></PermissionGuard>
              } />
              <Route path="roles/:roleId/edit" element={
                <PermissionGuard permission={PERMISSIONS.SETTINGS_ROLES_EDIT}><RoleEditorPage /></PermissionGuard>
              } />
              <Route path="subscriptions" element={
                <PermissionGuard permission={PERMISSIONS.SETTINGS_PAYMENTS}><SubscriptionsPage /></PermissionGuard>
              } />
              <Route path="invoices" element={
                <PermissionGuard permission={PERMISSIONS.SETTINGS_PAYMENTS}><InvoiceHistoryPage /></PermissionGuard>
              } />
            </Route>
            
            {/* Default redirect for /company/:slug */}
            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>

          {/* Fallback routes */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
      </AuthProvider>
    </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
